import React, { useEffect, useState, useRef } from 'react';
import {
  getUrlParams,
  usePageOnShowOrHide,
  obsScrollToBottom,
  useHasMore,
} from '@/utils/utils';
import PageNavbar from '@/components/Navbar';
import './index.less';
import { CatRecords, requestCatListApi } from '@/api/catList';
import { FromNativeOrWxParam } from '@/interface/downAppType';
import CatListItem from '@/components/CatListItem';
import { catListUBT } from '@/utils/pageUbt';

function CatListPageComponent() {
  const baseRequestConfig = useRef({
    pageSize: 10,
    lastId: '',
    isRequest: false,
  });
  const [onShow] = usePageOnShowOrHide();
  const list = useRef<CatRecords[]>([]);
  const [catList, setCatList] = useState<CatRecords[]>([]);
  const [hasMore] = useHasMore(50);
  const [showMore, setShowMore] = useState(false);

  const {
    businessId = '',
    type = '1',
    source = '',
  } = getUrlParams(
    (window.location.href || '').replace(/#\//g, '')
  ) as unknown as FromNativeOrWxParam;

  useEffect(() => {
    if (hasMore) {
      setShowMore(true);
    }
  }, [hasMore]);

  const getCatList = (init = false) => {
    const {
      pageSize,
      lastId = '',
      isRequest,
    } = baseRequestConfig?.current || {};

    // 检查是否已经有请求正在进行，如果是且此次请求非初始化，则返回false终止请求
    if (isRequest && !init) {
      return false;
    }
    // 更新请求配置，设置请求状态为true，并在初始化请求时重置当前页码为1
    baseRequestConfig.current = {
      ...baseRequestConfig.current,
      isRequest: true,
      ...(init ? { lastId: '' } : {}),
    };

    return requestCatListApi({
      businessId,
      businessType: +type,
      lastId: init ? '' : lastId,
      pageSize,
    })
      .then(({ data = {} }) => {
        const { records = [] } = data;
        const newData = [...(init ? [] : list.current), ...records];
        // 根据初始化状态，合并新旧数据
        setCatList(newData);
        list.current = newData;
        // 如果有新数据，则更新页码和请求状态
        if (records.length > 0) {
          baseRequestConfig.current = {
            ...baseRequestConfig.current,
            lastId: records[records.length - 1]?.id || '',
            isRequest: false,
          };
        }
      })
      .catch(() => {
        baseRequestConfig.current = {
          ...baseRequestConfig.current,
          isRequest: false,
        };
      });
  };

  const initListRequest = (init: boolean) => {
    getCatList(init);
  };

  useEffect(() => {
    if (catList.length > 0) {
      setTimeout(() => {
        const intersectionObserver = new IntersectionObserver((entries) => {
          entries.forEach((item: any) => {
            if (item.intersectionRatio >= 1) {
              const {
                id = '',
                sponsorid,
                assistStatus = '',
              } = item.target?.dataset || {};
              const parseSponsorId = JSON.parse((sponsorid as string) || '{}');
              catListUBT.moduleExpose({
                businessInfo: {
                  cathouseId: businessId,
                  businessId,
                  type,
                  status: assistStatus,
                  cat_id: id,
                  sponsorid: parseSponsorId && parseSponsorId?.join(','),
                  sponsored: parseSponsorId && parseSponsorId?.length > 0,
                },
                moduleId: 'miaowa_app_catlist_show',
              });
            }
          });
        });
        // 开始监听
        const items: any = document.querySelectorAll('.cat_list_item') || [];
        items.forEach((item: any) => {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
          intersectionObserver.observe(item);
        });
      }, 1000);
    }
  }, [catList]);

  useEffect(() => {
    obsScrollToBottom(() => initListRequest(false));
    catListUBT.pageView({
      cathouseId: businessId,
      businessId,
      type,
      source,
    });

    return () => {
      catListUBT.pageViewOut({
        cathouseId: businessId,
        businessId,
        type,
        source,
      });
      list.current = [];
      baseRequestConfig.current = {
        pageSize: 10,
        lastId: '',
        isRequest: false,
      };
    };
  }, []);

  const onCatListReady = () => {
    const { getCatListPromise } = window as any;

    if (getCatListPromise) {
      // 更新请求配置，设置请求状态为true，并在初始化请求时重置当前页码为1
      baseRequestConfig.current = {
        ...baseRequestConfig.current,
        isRequest: true,
        lastId: '',
      };

      getCatListPromise.then((res: any) => {
        const { records = [] } = res?.data || {};
        // 根据初始化状态，合并新旧数据
        setCatList(records as CatRecords[]);
        list.current = records;
        // 如果有新数据，则更新页码和请求状态
        if (records.length > 0) {
          baseRequestConfig.current = {
            ...baseRequestConfig.current,
            lastId: records[records.length - 1]?.id || '',
            isRequest: false,
          };
        }
      });
    }
  };

  useEffect(() => {
    if (onShow && businessId) {
      onCatListReady();
    }
  }, [onShow, businessId]);

  return (
    <div className="cat_list">
      <PageNavbar isBlack title="猫猫"></PageNavbar>
      <div className="cat_list_body">
        {(!showMore && catList.length > 6 ? catList.slice(0, 6) : catList).map(
          (item, index) => (
            <CatListItem
              businessId={businessId}
              key={`${item.id}${index}`}
              data={item}
            />
          )
        )}
      </div>
    </div>
  );
}

export default CatListPageComponent;
